import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { setCachedOnboardingStatus } from "../../platform/onboardingStatus";
import { getSessionHint } from "../../platform/sessionHint";

function headers() {
  const token = sessionStorage.getItem("syrakis_token");
  return { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function MfaOnboardingPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/mfa/status", { credentials: "same-origin", headers: headers() })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || "No se pudo consultar MFA.");
        setStatus(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function startEnrollment() {
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/mfa/enroll/start", { method: "POST", credentials: "same-origin", headers: headers() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo iniciar MFA.");
      setSetup(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function verifyEnrollment(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/mfa/enroll/verify", { method: "POST", credentials: "same-origin", headers: headers(), body: JSON.stringify({ code }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo verificar MFA.");
      setRecoveryCodes(data.recovery_codes || []);
      setStatus((current) => ({ ...current, enrolled: true }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function finish() {
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/onboarding/complete", { method: "POST", credentials: "same-origin", headers: headers() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo completar el onboarding.");
      setCachedOnboardingStatus(getSessionHint()?.session_key || `legacy:${sessionStorage.getItem("syrakis_token")}`, "completed");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function continueWithoutMfa() {
    if (!window.confirm("¿Continuar sin MFA obligatorio por ahora? La política dejará de exigir MFA a superadmin y admin. Podrás configurar el autenticador después.")) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch("/api/onboarding/mfa/skip", { method: "POST", credentials: "same-origin", headers: headers() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo continuar sin MFA.");
      setCachedOnboardingStatus(getSessionHint()?.session_key || `legacy:${sessionStorage.getItem("syrakis_token")}`, "completed");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="dashboard-main-body"><div className="card p-24">Cargando configuración MFA...</div></div>;

  return <div className="dashboard-main-body"><div className="row justify-content-center"><div className="col-xxl-8 col-xl-9">
    <div className="mb-24"><span className="text-sm text-primary-600 fw-semibold">Primer acceso · Paso 2 de 2</span><h4 className="mt-8 mb-8">Protege tu cuenta de superadmin</h4><p className="text-secondary-light mb-0">MFA TOTP está habilitado para superadmin y admin. Puedes configurarlo ahora o continuar sin MFA y activarlo después.</p></div>
    {error && <div className="alert alert-danger mb-24">{error}</div>}
    {!setup && !status?.enrolled && <section className="card p-24"><div className="d-flex align-items-start gap-3"><Icon icon="solar:shield-check-linear" className="text-primary-600 text-3xl" /><div><h6>Configurar autenticador</h6><p className="text-secondary-light">Usa Google Authenticator, Microsoft Authenticator, Authy u otra aplicación compatible con TOTP.</p><button type="button" className="btn btn-primary" onClick={startEnrollment} disabled={saving}>{saving ? "Preparando..." : "Generar configuración MFA"}</button></div></div></section>}
    {setup && !status?.enrolled && <section className="card p-24"><h6 className="mb-8">Escanea el código QR con tu autenticador</h6><div className="d-flex flex-column flex-md-row align-items-center gap-24 mb-20"><div className="border rounded p-12 bg-white"><img src={setup.qr_data_url} width="240" height="240" alt="Código QR para configurar MFA TOTP" /></div><div><p className="text-secondary-light">Usa Google Authenticator, Microsoft Authenticator, Authy u otra aplicación compatible.</p><p className="text-secondary-light mb-4">Si no puedes escanearlo, ingresa manualmente este secreto:</p><div className="alert alert-info"><strong>{setup.secret}</strong></div></div></div><details className="mb-20"><summary className="text-primary-600 cursor-pointer">Mostrar URI de configuración</summary><code className="d-block bg-neutral-50 p-12 radius-8 text-break mt-12">{setup.otpauth_uri}</code></details><form onSubmit={verifyEnrollment}><label className="form-label fw-semibold" htmlFor="totp-code">Código de 6 dígitos</label><input id="totp-code" className="form-control mb-16" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} required /><button type="submit" className="btn btn-success" disabled={saving}>{saving ? "Verificando..." : "Verificar MFA"}</button></form></section>}
    {status?.enrolled && recoveryCodes.length > 0 && <section className="card p-24"><h6 className="mb-8">Guarda tus códigos de recuperación</h6><p className="text-secondary-light">Se muestran una sola vez. Guárdalos en un lugar seguro.</p><div className="row g-2 mb-20">{recoveryCodes.map((value) => <div className="col-sm-6" key={value}><code className="d-block bg-neutral-50 p-10 radius-8">{value}</code></div>)}</div><button type="button" className="btn btn-primary" onClick={finish} disabled={saving}>{saving ? "Finalizando..." : "He guardado mis códigos y entrar al Core"}<Icon icon="solar:arrow-right-linear" className="ms-2" /></button></section>}
    {status?.enrolled && recoveryCodes.length === 0 && <section className="card p-24"><div className="alert alert-success">MFA TOTP ya está configurado para esta cuenta.</div><button type="button" className="btn btn-primary" onClick={finish} disabled={saving}>Completar onboarding</button></section>}
    {!status?.enrolled && <section className="card p-24 mt-24"><h6>Continuar sin MFA obligatorio</h6><p className="text-secondary-light">Deja de exigir MFA a superadmin y admin para completar el primer acceso. El enrolamiento seguirá disponible para configurarlo después. Esta excepción solo puede aplicarse durante el onboarding inicial.</p><button type="button" className="btn btn-outline-secondary" onClick={continueWithoutMfa} disabled={saving}>{saving ? "Continuando..." : "Omitir MFA y continuar"}</button></section>}
  </div></div></div>;
}
